import { useEffect, useMemo, useRef, useState } from 'react';
import { GLOSSARY } from '../data/glossary';
import { useChime } from '../hooks/useChime';
import styles from './ReverseMode.module.css';

interface ReverseModeProps {
  rounds?: number;
  onDone: (stats: { correct: number; total: number }) => void;
  onExit: () => void;
}

function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ');
}

/** Levenshtein distance, capped at 3 for speed. */
function withinTwoEdits(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 2) return false;
  const m = a.length;
  const n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr.push(Math.min(curr[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost));
    }
    prev = curr;
  }
  return prev[n]! <= 2;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function ReverseMode({ rounds = 8, onDone, onExit }: ReverseModeProps) {
  const chime = useChime();
  const deck = useMemo(
    () => shuffle(GLOSSARY.map((_, i) => i)).slice(0, rounds),
    [rounds]
  );

  const [round, setRound] = useState(0);
  const [direction, setDirection] = useState<'term' | 'definition'>('definition'); // shown side
  const [answer, setAnswer] = useState('');
  const [revealed, setRevealed] = useState<null | 'right' | 'wrong'>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Random direction per round
    setDirection(Math.random() < 0.5 ? 'term' : 'definition');
    setAnswer('');
    setRevealed(null);
    window.setTimeout(() => inputRef.current?.focus(), 50);
  }, [round]);

  const termIndex = deck[round];
  const card = termIndex != null ? GLOSSARY[termIndex] : null;
  if (!card) return null;

  const prompt = direction === 'term' ? card.term : card.definition;
  const expected = direction === 'term' ? card.definition : card.term;
  const promptLabel = direction === 'term' ? 'the term' : 'the definition';
  const inputLabel = direction === 'term' ? 'what does it mean?' : 'name the term';

  function check() {
    if (revealed !== null) return;
    const normUser = normalize(answer);
    const normExpected = normalize(expected);
    // Acronyms: case-sensitive full equality also OK
    const isCorrect =
      normUser === normExpected ||
      withinTwoEdits(normUser, normExpected) ||
      // For definitions, check if all the keywords are present (rough match)
      (direction === 'term' && normUser.length > 8 && normExpected.includes(normUser));
    if (isCorrect) {
      chime.play('match');
      setRevealed('right');
      setCorrectCount((c) => c + 1);
    } else {
      chime.play('miss');
      setRevealed('wrong');
    }
  }

  function next() {
    if (round + 1 < deck.length) {
      setRound((r) => r + 1);
    } else {
      chime.play('win');
      onDone({ correct: correctCount + (revealed === 'right' ? 0 : 0), total: deck.length });
    }
  }

  function skip() {
    setRevealed('wrong');
  }

  return (
    <section className={styles.frame}>
      <header className={styles.header}>
        <span className={styles.label}>reverse · round {round + 1} of {deck.length}</span>
        <div className={styles.metrics}>
          <span className={styles.metric}>correct: {correctCount}</span>
          <button type="button" className={styles.exit} onClick={onExit}>
            exit
          </button>
        </div>
      </header>

      <div className={styles.card}>
        <p className={styles.promptLabel}>{promptLabel}</p>
        <p className={direction === 'term' ? styles.promptTerm : styles.promptDefinition}>
          {prompt}
        </p>
      </div>

      <form
        className={styles.row}
        onSubmit={(e) => {
          e.preventDefault();
          if (revealed === null) check();
          else next();
        }}
      >
        <label htmlFor="reverse-input" className={styles.inputLabel}>
          {inputLabel}
        </label>
        <input
          id="reverse-input"
          ref={inputRef}
          type="text"
          className={`${styles.input} ${revealed === 'right' ? styles.inputRight : ''} ${
            revealed === 'wrong' ? styles.inputWrong : ''
          }`}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          disabled={revealed !== null}
          autoComplete="off"
          maxLength={200}
        />
        <div className={styles.actions}>
          {revealed === null && (
            <>
              <button type="submit" className={styles.primary}>
                check
              </button>
              <button type="button" className={styles.secondary} onClick={skip}>
                skip
              </button>
            </>
          )}
          {revealed !== null && (
            <button type="submit" className={styles.primary}>
              {round + 1 < deck.length ? 'next →' : 'finish'}
            </button>
          )}
        </div>
      </form>

      {revealed && (
        <div className={`${styles.reveal} ${revealed === 'right' ? styles.right : styles.wrong}`}>
          {revealed === 'right' ? (
            <p>correct.</p>
          ) : (
            <>
              <p>the answer was:</p>
              <p className={styles.expected}>{expected}</p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
